import React, { useState, useContext, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { ReactEditor, useSlate } from 'slate-react';
import { Editor, Node } from 'slate';
import { TagContext } from 'app/state/contexts/TagContext';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { ActiveDotContext } from '../contexts/ActiveDotContext';
import { getTagHighlightColor, getTagIndicatorColor } from '../utils/colors';
import { getTagNestingDepth, navigateToTag, isTag } from '../utils/navigation';
import { collectNestedTagIds } from '../utils/overlap';
import TagDotIndicators from './TagDotIndicators';
import { TagElement as TagElementType } from '../types';

interface TagElementProps {
  attributes: Record<string, unknown>;
  children: React.ReactNode;
  element: TagElementType;
  isPDF?: boolean;
}

export const TagElement = ({ attributes, children, element, isPDF }: TagElementProps) => {
  const editor = useSlate();
  const { highlightedOption, tagDataMap, setClickedTagIds, visibilityOption, extensionConceptIds, termHighlightTagIds, calcPickMode } = useContext(TagContext);
  const { setIsSidebarOpen } = useContext(SidebarContext);
  const { isEditingMode, taggingSubMode } = useEditorMode();
  const isCoverageMode = taggingSubMode === 'mark-coverage';
  const [hover, setHover] = useState(false);
  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  const parentDotActive = useContext(ActiveDotContext);

  const spanRef = useRef<HTMLSpanElement>(null);
  const [indicatorHeight, setIndicatorHeight] = useState<number | null>(null);

  const composedRef = useCallback(
    (node: HTMLSpanElement | null) => {
      (spanRef as React.RefObject<HTMLSpanElement | null>).current = node;
      const slateRef = (attributes as { ref?: React.Ref<HTMLSpanElement> }).ref;
      if (typeof slateRef === 'function') slateRef(node);
      else if (slateRef && typeof slateRef === 'object') (slateRef as React.RefObject<HTMLSpanElement | null>).current = node;
    },
    [attributes]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (spanRef.current) {
      const rects = spanRef.current.getClientRects();
      if (rects.length > 0) {
        setIndicatorHeight(rects[0].height);
      }
    }
  });

  const tagData = tagDataMap[element.tagId];
  const activeTagData = tagDataMap[activeTagId ?? element.tagId];
  const isExtensionTag = !!(tagData?.conceptId && extensionConceptIds?.has(tagData.conceptId));
  const visibilityKey = isExtensionTag ? 'extension' : 'core';
  const isHiddenByVisibility = Array.isArray(visibilityOption) && !visibilityOption.includes(visibilityKey);

  const inPickMode = !!calcPickMode;
  const isPickParentCell =
    inPickMode && (calcPickMode.parentTagId === element.tagId || (!!tagData?.conceptId && calcPickMode.parentConceptId === tagData.conceptId));
  const isPickedTerm = inPickMode && !!tagData?.conceptId && calcPickMode.termConceptIds?.has(tagData.conceptId);
  const isPickedCell = isPickedTerm && calcPickMode.pickedTagIds?.has(element.tagId);
  const isTermHighlighted = !inPickMode && termHighlightTagIds?.has(element.tagId);

  let tagHighlightColor = isHiddenByVisibility ? 'transparent' : isExtensionTag ? '#ffd8f5' : getTagHighlightColor(highlightedOption, activeTagData);
  let tagIndicatorColor = isHiddenByVisibility ? 'transparent' : isExtensionTag ? '#ff9de7' : getTagIndicatorColor(highlightedOption, activeTagData);
  if (!isHiddenByVisibility) {
    if (isPickedCell || isTermHighlighted) {
      tagHighlightColor = '#c4e8c9';
      tagIndicatorColor = '#3d7a4f';
    } else if (isPickedTerm) {
      tagHighlightColor = '#e8f3ea';
      tagIndicatorColor = '#a8cbb0';
    } else if (inPickMode && !isPickParentCell) {
      tagHighlightColor = '#fdebc8';
      tagIndicatorColor = '#d9a03f';
    }
  }

  let tagPath: number[] | null;
  try {
    tagPath = ReactEditor.findPath(editor, element);
  } catch {
    tagPath = null;
  }

  const nestingDepth = tagPath ? getTagNestingDepth(editor, tagPath) : 0;

  const isContinuation = !!tagData?.predecessorId;
  const canContinue = tagData?.tagType !== 'nonFraction';

  const activeIsContinuation = activeTagId ? !!activeTagData?.predecessorId : isContinuation;
  const successorId = activeTagId ? activeTagData?.nextSegment : element.continuedAt ?? tagData?.nextSegment;
  const continuesToSuccessor = !!successorId;

  const isInsideDifferentConceptTag = (path: number[] | null): boolean => {
    if (!path) return false;
    return Array.from(Node.ancestors(editor, path)).some(([n]) => isTag(n) && (n as TagElementType).tagType !== element.tagType);
  };
  const findTagPath = (tagId: string | undefined | null): number[] | null => {
    if (!tagId) return null;
    const entry = Array.from(Editor.nodes(editor, { at: [], match: (n) => isTag(n) && (n as TagElementType).tagId === tagId }))[0];
    return entry ? (entry[1] as number[]) : null;
  };
  const linkCrossesDifferentTag = (partnerId: string | undefined | null): boolean =>
    !!partnerId && (isInsideDifferentConceptTag(tagPath) || isInsideDifferentConceptTag(findTagPath(partnerId)));
  const successorIsOverlap = continuesToSuccessor ? linkCrossesDifferentTag(successorId) : false;
  const predecessorIsOverlap = activeIsContinuation ? linkCrossesDifferentTag(activeTagData?.predecessorId) : false;

  const allTagIds = useMemo(() => (nestingDepth === 0 ? [element.tagId, ...collectNestedTagIds(element)] : null), [nestingDepth, element]);
  if (isPDF || isEditingMode) {
    return (
      <span
        {...attributes}
        id={element.tagId}
        data-tag={element.tagType}
        data-tag-continued-at={element.continuedAt || undefined}
        data-tag-continued-from={tagData?.predecessorId || undefined}
        className="ixbrl-tag"
      >
        {children}
      </span>
    );
  }

  return (
    <span
      {...attributes}
      ref={composedRef}
      id={element.tagId}
      data-tag={element.tagType}
      data-tag-continued-at={element.continuedAt || undefined}
      data-tag-continued-from={tagData?.predecessorId || undefined}
      className="ixbrl-tag"
      style={{
        backgroundColor: nestingDepth > 0 && parentDotActive ? 'transparent' : tagHighlightColor,
        position: 'relative',
        cursor: isHiddenByVisibility || isCoverageMode ? 'auto' : nestingDepth === 0 ? 'pointer' : undefined,
      }}
      onClick={
        !isHiddenByVisibility && nestingDepth === 0 && !isCoverageMode
          ? (e: React.MouseEvent) => {
              if (e.defaultPrevented) return;
              if (calcPickMode) {
                e.preventDefault();
                e.stopPropagation();
                if (isPickParentCell || !tagData?.conceptId) return;
                calcPickMode.onToggleTerm?.(tagData.conceptId, element.tagId);
                return;
              }
              const tagIdsAtPoint: string[] = [];
              let node = e.target as HTMLElement | null;
              while (node && node !== e.currentTarget) {
                if (node.classList?.contains('ixbrl-tag') && node.id) {
                  tagIdsAtPoint.push(node.id);
                }
                node = node.parentElement;
              }
              if (e.currentTarget instanceof HTMLElement && e.currentTarget.id) {
                tagIdsAtPoint.push(e.currentTarget.id);
              }
              const uniqueIds = Array.from(new Set(tagIdsAtPoint));
              setClickedTagIds(uniqueIds);
              setIsSidebarOpen(8);
            }
          : undefined
      }
    >
      {!isHiddenByVisibility && nestingDepth === 0 && allTagIds && (
        <TagDotIndicators
          allTagIds={allTagIds}
          highlightedOption={highlightedOption}
          tagDataMap={tagDataMap}
          onDotClick={setActiveTagId}
          activeTagId={activeTagId}
          indicatorHeight={indicatorHeight}
        />
      )}

      {!isHiddenByVisibility && (
        <span
          contentEditable={false}
          style={{
            position: 'absolute',
            zIndex: 1,
            top: '0',
            left: '-5px',
            height: indicatorHeight ? `${indicatorHeight}px` : '100%',
            backgroundColor: tagIndicatorColor,
            borderRadius: '13px 0 0 13px',
            padding: '0 2px',
            width: 'fit-content',
            minWidth: '1px',
            display: (nestingDepth > 0 && parentDotActive) || predecessorIsOverlap ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: activeIsContinuation && !isCoverageMode ? 'pointer' : 'default',
          }}
          onClick={(e: React.MouseEvent) => {
            if (isCoverageMode) return;
            if (isContinuation && tagData?.predecessorId) {
              e.preventDefault();
              e.stopPropagation();
              navigateToTag(editor, tagData.predecessorId);
            }
          }}
        >
          {activeIsContinuation && !predecessorIsOverlap ? <i className="fa-solid fa-arrow-left" style={{ fontSize: '8px', color: '#fff' }} /> : null}
        </span>
      )}

      {!isHiddenByVisibility && (
        <span
          contentEditable={false}
          style={{
            display: nestingDepth > 0 && parentDotActive ? 'none' : 'flex',
            position: 'absolute',
            zIndex: 1,
            bottom: '0',
            right: '-4px',
            height: indicatorHeight ? `${indicatorHeight}px` : '100%',
            backgroundColor: tagIndicatorColor,
            borderRadius: '0 13px 13px 0',
            width: 'fit-content',
            padding: ' 0 2px',
            minWidth: '1px',
            cursor: isCoverageMode ? 'default' : 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'width 0.3s ease-in-out, padding 0.3s ease-in-out',
          }}
          onMouseEnter={() => !isCoverageMode && setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            if (isCoverageMode) return;
            if (canContinue) {
              setClickedTagIds([element.tagId]);
              setIsSidebarOpen(8);
            }
          }}
        >
          {continuesToSuccessor ? (
            successorIsOverlap ? (
              <i className="fa-solid fa-arrows-left-right" style={{ fontSize: '8px', color: '#fff' }} />
            ) : (
              <i className="fa-solid fa-arrow-right" style={{ fontSize: '8px', color: '#fff' }} />
            )
          ) : hover && canContinue ? (
            <i className="fa-regular fa-plus fa-xs" style={{ fontSize: '8px', color: '#fff' }} />
          ) : null}
        </span>
      )}

      {activeTagId ? <ActiveDotContext.Provider value={true}>{children}</ActiveDotContext.Provider> : children}
    </span>
  );
};
